import { useEffect, useMemo, useState } from "react";
import {
  Container,
  Title,
  Text,
  Paper,
  Table,
  TextInput,
  Group,
  Button,
  Badge,
  Stack,
  Pagination,
  Avatar,
  Modal,
  Skeleton,
  Divider,
  Grid,
  ThemeIcon,
  Timeline,
  Center,
  LoadingOverlay,
  Select,
  Switch,
} from "@mantine/core";
import {
  IconSearch,
  IconArrowsSort,
  IconArrowUp,
  IconArrowDown,
  IconTrophy,
  IconDeviceMobile,
  IconBrandApple,
  IconBrandAndroid,
  IconCheck,
  IconDownload,
  IconListDetails,
} from "@tabler/icons-react";
import { Endpoints } from "../../../lib/endpoints";
import { PaginatedResponse, NonPaginatedResponse } from "../../../lib/types";
import { HttpCodes } from "../../../lib/data";
import {
  usePerformRequest,
  PerformRequest,
} from "../../../hooks/usePerformRequest";
import PermissionGuard from "../../../components/PermissionGuard";
import { AdminPermissions, virelCore } from "../../../lib/data";
import { IUserProps, IDeviceProps } from "../../../lib/types/User";
import { Tabs } from "@mantine/core";

const Leaderboard = () => {
  useEffect(() => {
    document.title = "Leaderboard | Virel Admin";
  }, []);

  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sortState, setSortState] = useState<{
    column: string;
    direction: "asc" | "desc";
  }>({ column: "totalCoinsEarned", direction: "desc" });

  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileData, setProfileData] = useState<IUserProps | null>(null);
  const [devices, setDevices] = useState<IDeviceProps[]>([]);
  const [activeTab, setActiveTab] = useState<string | null>("overview");

  // Squad data keyed by userId
  const [squadMap, setSquadMap] = useState<Record<string, any>>({});

  // Filter out any other fields, use GetLeaderboard
  const endpoint = useMemo(
    () =>
      Endpoints.GetLeaderboard({
        page: currentPage,
        limit: pageSize,
        search: debouncedSearch.trim() || undefined,
        sortBy: sortState.column,
        sortDir: sortState.direction,
      }),
    [currentPage, pageSize, debouncedSearch, sortState],
  );

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data, isLoading } = usePerformRequest<
    PaginatedResponse<
      {
        id: string;
        name: string;
        email?: string;
        username?: string;
        isVirelCore?: boolean;
        totalCoinsEarned: number;
        createdAt: string;
      }[]
    >
  >({
    method: endpoint.method,
    route: endpoint.route,
    showError: false,
  });

  const [leaderboardEnabled, setLeaderboardEnabled] = useState(true);
  const [updatingConfig, setUpdatingConfig] = useState(false);

  // Fetch Leaderboard Config
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await PerformRequest<NonPaginatedResponse<any>>({
          route: Endpoints.GetConfigByKey("leaderboard_enabled").route,
          method: Endpoints.GetConfigByKey("leaderboard_enabled").method,
        });

        if (res.status === HttpCodes.Success && res.data?.data) {
          // Explicitly check boolean parsing if stored as string occasionally
          setLeaderboardEnabled(
            res.data.data.value === true || res.data.data.value === "true",
          );
        }
      } catch (error) {
        console.error("Failed to fetch config", error);
      }
    };
    fetchConfig();
  }, []);

  const handleToggleLeaderboard = async (checked: boolean) => {
    setUpdatingConfig(true);
    setLeaderboardEnabled(checked); // Optimistic Update

    try {
      const endpoint = Endpoints.UpdateConfig("leaderboard_enabled");
      await PerformRequest({
        route: endpoint.route,
        method: endpoint.method,
        data: { value: checked },
      });
    } catch (error) {
      console.error("Failed to update leaderboard config", error);
      setLeaderboardEnabled(!checked); // Rollback optimistic update
    } finally {
      setUpdatingConfig(false);
    }
  };

  const toggleSort = (column: string) => {
    setSortState((prev) => {
      if (prev.column === column) {
        return {
          column,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      setCurrentPage(1);
      return { column, direction: "desc" };
    });
  };

  const sortIndicator = (column: string) => {
    if (sortState.column !== column) return <IconArrowsSort size={14} />;
    return sortState.direction === "asc" ? (
      <IconArrowUp size={14} />
    ) : (
      <IconArrowDown size={14} />
    );
  };

  const users = data?.data ?? [];
  const totalRecords = data?.totalRecords ?? 0;
  const totalPages = Math.max(1, Math.ceil((totalRecords || 0) / pageSize));

  // Fetch squad info for all users on the current page in parallel
  useEffect(() => {
    if (!users.length) return;
    const controller = new AbortController();
    const fetchSquads = async () => {
      const results = await Promise.allSettled(
        users.map((u) =>
          PerformRequest<NonPaginatedResponse<any>>({
            method: Endpoints.GetUserSquad(u.id).method,
            route: Endpoints.GetUserSquad(u.id).route,
          }).then((res) => ({ userId: u.id, data: res.data?.data ?? null }))
        )
      );
      if (controller.signal.aborted) return;
      const map: Record<string, any> = {};
      results.forEach((r) => {
        if (r.status === "fulfilled") {
          map[r.value.userId] = r.value.data;
        }
      });
      setSquadMap(map);
    };
    fetchSquads();
    return () => controller.abort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Modal Fetch Logic
  useEffect(() => {
    if (!selectedUserId) return;
    const fetchProfile = async () => {
      setProfileLoading(true);
      try {
        const [profileRes, deviceRes] = await Promise.all([
          PerformRequest<NonPaginatedResponse<IUserProps>>({
            method: Endpoints.GetUserProfile(selectedUserId).method,
            route: Endpoints.GetUserProfile(selectedUserId).route,
          }),
          PerformRequest<NonPaginatedResponse<IDeviceProps[]>>({
            method: Endpoints.GetUserDevices(selectedUserId).method,
            route: Endpoints.GetUserDevices(selectedUserId).route,
          }),
        ]);
        if (profileRes.status === HttpCodes.Success) {
          setProfileData(profileRes.data?.data || null);
        }
        if (deviceRes.status === HttpCodes.Success) {
          setDevices(deviceRes.data?.data || []);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setProfileLoading(false);
      }
    };
    fetchProfile();
  }, [selectedUserId]);

  const getRankBadgeProps = (index: number) => {
    const rank = (currentPage - 1) * pageSize + index + 1;
    if (rank === 1) return { color: "yellow", title: "1st Place", rank };
    if (rank === 2) return { color: "gray", title: "2nd Place", rank };
    if (rank === 3) return { color: "orange", title: "3rd Place", rank };
    return { color: "blue", title: `#${rank}`, rank };
  };

  const rangeStart = totalRecords === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const rangeEnd = Math.min(currentPage * pageSize, totalRecords);

  const getSquadCell = (userId: string) => {
    if (!(userId in squadMap)) {
      return <Skeleton height={18} width={80} />;
    }
    const sd = squadMap[userId];
    if (!sd || !sd.squad) {
      return <Text size="sm" c="dimmed">—</Text>;
    }
    const name: string = sd.squad?.name ?? "Squad";
    const role: string = sd.role ?? "member";
    const status: string = sd.myMembershipStatus ?? "active";

    let color: string;
    if (role === "leader") {
      color = "green";
    } else if (status === "active") {
      color = "blue";
    } else {
      color = "orange";
    }

    return (
      <Badge color={color} variant="light" size="sm" maw={120} style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {name}
      </Badge>
    );
  };

  return (
    <PermissionGuard
      permission={AdminPermissions.MANAGE_USERS}
      fallback={
        <Container>
          <Text>Unauthorized</Text>
        </Container>
      }
    >
      <div className="users-page">
        <Container size="xl">
          <Stack gap="lg">
            <Group justify="space-between" align="flex-start">
              <div>
                <Title order={1} className="page-title">
                  <Group gap={8}>
                    <IconTrophy size={32} color="#FFD700" />
                    Leaderboard
                  </Group>
                </Title>
                <Text size="sm" c="dimmed" mt={4}>
                  Top earning users on the platform
                </Text>
              </div>
              <Paper p="xs" withBorder radius="md">
                <Group gap="sm">
                  <Text size="sm" fw={600}>
                    Leaderboard Visibility
                  </Text>
                  <Switch
                    checked={leaderboardEnabled}
                    onChange={(e) =>
                      handleToggleLeaderboard(e.currentTarget.checked)
                    }
                    size="md"
                    color="green"
                    disabled={updatingConfig}
                  />
                </Group>
              </Paper>
            </Group>

            <Paper shadow="sm" p="md" radius="md" withBorder>
              <Group gap="md" mb="md" className="table-toolbar">
                <TextInput
                  placeholder="Search by name, username, or email..."
                  leftSection={<IconSearch size={16} />}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.currentTarget.value)}
                  style={{ flex: 1 }}
                  size="sm"
                />
                <Group gap="xs" align="center">
                  <Text size="sm" c="dimmed">
                    List Per Page
                  </Text>
                  <Select
                    data={["5", "10", "25", "50", "100"]}
                    value={String(pageSize)}
                    onChange={(value) => {
                      const next = Number(value) || 25;
                      setPageSize(next);
                      setCurrentPage(1);
                    }}
                    size="sm"
                    w={100}
                  />
                </Group>
              </Group>

              <div className="table-container">
                <Table
                  striped
                  highlightOnHover
                  verticalSpacing="xs"
                  withColumnBorders
                >
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Rank</Table.Th>
                      <Table.Th
                        className="sortable"
                        onClick={() => toggleSort("firstName")}
                      >
                        <Group gap={6}>User {sortIndicator("firstName")}</Group>
                      </Table.Th>
                      <Table.Th>Username</Table.Th>
                      <Table.Th
                        className="sortable"
                        onClick={() => toggleSort("totalCoinsEarned")}
                      >
                        <Group gap={6}>
                          Coins {sortIndicator("totalCoinsEarned")}
                        </Group>
                      </Table.Th>
                      <Table.Th
                        className="sortable"
                        onClick={() => toggleSort("createdAt")}
                      >
                        <Group gap={6}>
                          Joined {sortIndicator("createdAt")}
                        </Group>
                      </Table.Th>
                      <Table.Th>Squad</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {isLoading ? (
                      <Table.Tr>
                        <Table.Td colSpan={6}>
                          <Skeleton height={200} />
                        </Table.Td>
                      </Table.Tr>
                    ) : users.length > 0 ? (
                      users.map((user, index) => {
                        const rankProps = getRankBadgeProps(index);
                        const isVirelCore = user.isVirelCore || (user.email && virelCore.includes(user.email));
                        return (
                          <Table.Tr
                            key={user.id}
                            style={{ cursor: "pointer" }}
                            onClick={() => setSelectedUserId(user.id)}
                            className={isVirelCore ? "virel-core-row" : ""}
                          >
                            <Table.Td>
                              {rankProps.rank <= 3 ? (
                                <Badge
                                  color={rankProps.color}
                                  size="sm"
                                  variant="light"
                                >
                                  {rankProps.title}
                                </Badge>
                              ) : (
                                <Text size="sm" fw={600} c="dimmed">
                                  #{rankProps.rank}
                                </Text>
                              )}
                            </Table.Td>
                            <Table.Td>
                              <Group gap="sm">
                                <Avatar size="sm" color="brand" radius="sm">
                                  {user.name.charAt(0)}
                                </Avatar>
                                <div>
                                  <Text size="sm" fw={500}>
                                    {user.name}
                                  </Text>
                                  {user.email && (
                                    <Text size="xs" c="dimmed">
                                      {user.email}
                                    </Text>
                                  )}
                                </div>
                              </Group>
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm">
                                {user.username ? `@${user.username}` : "—"}
                              </Text>
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm" fw={600} c="green">
                                {user.totalCoinsEarned.toLocaleString()}
                              </Text>
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm">
                                {new Date(user.createdAt).toLocaleDateString()}
                              </Text>
                            </Table.Td>
                            <Table.Td>{getSquadCell(user.id)}</Table.Td>
                          </Table.Tr>
                        );
                      })
                    ) : (
                      <Table.Tr>
                        <Table.Td colSpan={6} ta="center" py="xl">
                          <Text c="dimmed">No users found</Text>
                        </Table.Td>
                      </Table.Tr>
                    )}
                  </Table.Tbody>
                </Table>
              </div>

              <Group justify="center" mt="md">
                <Pagination
                  value={currentPage}
                  onChange={setCurrentPage}
                  total={totalPages}
                  size="sm"
                />
              </Group>

              <Group
                justify="space-between"
                mt="md"
                pt="md"
                style={{ borderTop: "1px solid #e2e8f0" }}
              >
                <Text size="sm" c="dimmed">
                  Showing {rangeStart} to {rangeEnd} of {totalRecords} users
                </Text>
              </Group>
            </Paper>
          </Stack>
        </Container>

        {/* Beautiful Modal Component */}
        <Modal
          opened={!!selectedUserId}
          onClose={() => {
            setSelectedUserId(null);
            setProfileData(null);
            setActiveTab("overview");
          }}
          title={
            <Text fw={800} size="xl">
              User Comprehensive Profile
            </Text>
          }
          size="75rem"
          centered
          overlayProps={{ blur: 10 }}
        >
          <LoadingOverlay visible={profileLoading} />
          {profileData ? (
            <Stack gap="lg">
              {/* Header section with photo, name & status */}
              <Paper p="xl" radius="md" withBorder bg="var(--bg-surface-2)">
                <Group align="flex-start" justify="space-between">
                  <Group gap="xl">
                    <Avatar size={100} radius={100} color="blue">
                      {(
                        profileData.firstName ||
                        profileData.email ||
                        "?"
                      ).charAt(0)}
                    </Avatar>
                    <Stack gap={4}>
                      <Title order={2}>
                        {[profileData.firstName, profileData.lastName]
                          .filter(Boolean)
                          .join(" ") || profileData.email}
                      </Title>
                      <Text c="dimmed" size="lg">
                        {profileData.email}
                      </Text>
                      <Group mt="xs">
                        <Badge
                          color={profileData.isActive ? "green" : "gray"}
                          size="lg"
                        >
                          {profileData.isActive ? "Active User" : "Inactive"}
                        </Badge>
                        <Badge
                          color="yellow"
                          variant="filled"
                          leftSection={<IconTrophy size={14} />}
                          size="lg"
                        >
                          Level {profileData.level || 1}
                        </Badge>
                      </Group>
                    </Stack>
                  </Group>
                  <Stack align="flex-end" gap={4}>
                    <Text size="sm" c="dimmed">
                      Joined Platform:
                    </Text>
                    <Text fw={600}>
                      {new Date(profileData.createdAt).toLocaleDateString()}
                    </Text>
                    <Text size="sm" c="dimmed" mt="xs">
                      Total Earnings:
                    </Text>
                    <Text fw={800} size="xl" c="green">
                      {(profileData.totalCoinsEarned || 0).toLocaleString()}{" "}
                      Coins
                    </Text>
                  </Stack>
                </Group>
              </Paper>

              <Tabs
                value={activeTab}
                onChange={setActiveTab}
                variant="pills"
                radius="md"
              >
                <Tabs.List mb="md" grow>
                  <Tabs.Tab value="overview">Overview</Tabs.Tab>
                  <Tabs.Tab value="financials">Financials</Tabs.Tab>
                  <Tabs.Tab value="opportunities">Opportunities</Tabs.Tab>
                  <Tabs.Tab value="devices">Device History</Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="overview">
                  <Grid>
                    <Grid.Col span={6}>
                      <Paper p="md" withBorder radius="md">
                        <Title order={5} mb="sm">
                          General Information
                        </Title>
                        <Divider mb="sm" />
                        <Stack gap="xs">
                          <Group justify="space-between">
                            <Text c="dimmed" size="sm">
                              Phone Number
                            </Text>
                            <Text fw={500}>
                              {profileData.phoneNumber || "—"}
                            </Text>
                          </Group>
                          <Group justify="space-between">
                            <Text c="dimmed" size="sm">
                              Level
                            </Text>
                            <Text fw={500}>{profileData.level ?? 1}</Text>
                          </Group>
                          <Group justify="space-between">
                            <Text c="dimmed" size="sm">
                              Referral Code
                            </Text>
                            <Text fw={500}>
                              {profileData.referralCode || "—"}
                            </Text>
                          </Group>
                          <Group justify="space-between">
                            <Text c="dimmed" size="sm">
                              Last Login
                            </Text>
                            <Text fw={500}>
                              {profileData.lastLoginAt
                                ? new Date(
                                    profileData.lastLoginAt,
                                  ).toLocaleString()
                                : "Never"}
                            </Text>
                          </Group>
                        </Stack>
                      </Paper>
                    </Grid.Col>
                    <Grid.Col span={6}>
                      <Paper p="md" withBorder radius="md">
                        <Title order={5} mb="sm">
                          Profile Progress
                        </Title>
                        <Divider mb="sm" />
                        <Stack gap="xs">
                          <Group justify="space-between">
                            <Text c="dimmed" size="sm">
                              Email Verified
                            </Text>
                            <Badge
                              color={
                                profileData.isEmailVerified ? "green" : "red"
                              }
                            >
                              {profileData.isEmailVerified ? "Yes" : "No"}
                            </Badge>
                          </Group>

                          <Group justify="space-between">
                            <Text c="dimmed" size="sm">
                              Current Streak
                            </Text>
                            <Text fw={500}>{profileData.streakCount || 0}</Text>
                          </Group>
                          <Group justify="space-between">
                            <Text c="dimmed" size="sm">
                              Longest Streak
                            </Text>
                            <Text fw={500}>
                              {profileData.longestStreak || 0}
                            </Text>
                          </Group>
                        </Stack>
                      </Paper>
                    </Grid.Col>
                  </Grid>
                </Tabs.Panel>

                <Tabs.Panel value="financials">
                  <Grid>
                    <Grid.Col span={6}>
                      <Paper
                        p="xl"
                        radius="md"
                        withBorder
                        ta="center"
                        bg="var(--bg-surface-2)"
                      >
                        <Text size="lg" c="dimmed" mb="xs">
                          Current Balance
                        </Text>
                        <Text fw={800} size="32px" c="green">
                          ₦{(profileData.balance || 0).toLocaleString()}
                        </Text>
                      </Paper>
                    </Grid.Col>
                    <Grid.Col span={6}>
                      <Paper
                        p="xl"
                        radius="md"
                        withBorder
                        ta="center"
                        bg="var(--bg-surface-2)"
                      >
                        <Text size="lg" c="dimmed" mb="xs">
                          Total Withdrawn
                        </Text>
                        <Text fw={800} size="32px">
                          ₦{0 /* Pending proper API calculation */}
                        </Text>
                      </Paper>
                    </Grid.Col>
                  </Grid>
                  {profileData.bankAccount ? (
                    <Paper p="md" withBorder mt="xl">
                      <Title order={5} mb="md">
                        Banking Information
                      </Title>
                      <Group justify="space-between">
                        <div>
                          <Text fw={600} size="lg">
                            {profileData.bankAccount.bankName}
                          </Text>
                          <Text>{profileData.bankAccount.accountName}</Text>
                          <Text c="dimmed" size="sm">
                            {profileData.bankAccount.accountNumber}
                          </Text>
                        </div>
                        <Badge color="green" size="lg">
                          Verified Account
                        </Badge>
                      </Group>
                    </Paper>
                  ) : (
                    <Paper p="xl" withBorder mt="xl" ta="center">
                      <Text c="dimmed">No bank account linked</Text>
                    </Paper>
                  )}
                </Tabs.Panel>

                <Tabs.Panel value="opportunities">
                  <Paper p="xl" radius="md" withBorder ta="center" h={200}>
                    <Group justify="center" h="100%">
                      <Stack align="center">
                        <ThemeIcon
                          size={64}
                          radius="xl"
                          color="gray"
                          variant="light"
                        >
                          <IconListDetails size={32} />
                        </ThemeIcon>
                        <Text c="dimmed" size="sm" mt="sm">
                          Fetching Opportunities Data is performed in a separate
                          tab...
                        </Text>
                      </Stack>
                    </Group>
                  </Paper>
                </Tabs.Panel>

                <Tabs.Panel value="devices">
                  <Stack>
                    <Title order={4}>Registered Devices</Title>
                    {devices.length === 0 ? (
                      <Text c="dimmed">No associated devices</Text>
                    ) : (
                      <Grid>
                        {devices.map((device) => (
                          <Grid.Col span={6} key={device._id}>
                            <Paper p="md" withBorder>
                              <Group>
                                <ThemeIcon
                                  size="xl"
                                  radius="md"
                                  color="blue"
                                  variant="light"
                                >
                                  {device.os === "iOS" ? (
                                    <IconBrandApple />
                                  ) : (
                                    <IconBrandAndroid />
                                  )}
                                </ThemeIcon>
                                <div style={{ flex: 1 }}>
                                  <Text fw={600}>
                                    {device.maker} {device.deviceModel}
                                  </Text>
                                  <Text size="xs" c="dimmed">
                                    OS: {device.os} {device.osVersion}
                                  </Text>
                                  <Text size="xs" c="dimmed">
                                    IP: {device.ipAddress}
                                  </Text>
                                </div>
                              </Group>
                              <Divider my="sm" />
                              <Group justify="space-between">
                                <Text size="xs" c="dimmed">
                                  Last active:
                                </Text>
                                <Text size="xs" fw={500}>
                                  {new Date(device.lastLogin).toLocaleString()}
                                </Text>
                              </Group>
                            </Paper>
                          </Grid.Col>
                        ))}
                      </Grid>
                    )}
                  </Stack>
                </Tabs.Panel>
              </Tabs>
            </Stack>
          ) : (
            <Center h={400}>No Data Available</Center>
          )}
        </Modal>
      </div>
    </PermissionGuard>
  );
};

export default Leaderboard;
